import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, formatApiError } from "../auth";
import { Plus, Copy, Eye, EyeOff, Trash2, Pencil, Search, UploadCloud } from "lucide-react";

/**
 * Admin — auto parts catalogue list. Mirrors AdminProducts.jsx (tyres) so the
 * ops team has a consistent workflow across product types.
 */
const AdminParts = () => {
  const [parts, setParts] = useState([]);
  const [taxonomy, setTaxonomy] = useState({ categories: [], conditions: [] });
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (category) params.category = category;
    adminApi.get("/parts", { params })
      .then((r) => setParts(r.data))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);
  useEffect(() => { adminApi.get("/parts/taxonomy").then((r) => setTaxonomy(r.data)).catch(() => {}); }, []);

  const doDelete = async (id) => {
    if (!window.confirm("Delete this part?")) return;
    try { await adminApi.delete(`/parts/${id}`); load(); } catch (e) { alert(formatApiError(e)); }
  };
  const doDuplicate = async (id) => {
    try { await adminApi.post(`/parts/${id}/duplicate`); load(); } catch (e) { alert(formatApiError(e)); }
  };
  const doToggle = async (id) => {
    try { await adminApi.post(`/parts/${id}/toggle-visibility`); load(); } catch (e) { alert(formatApiError(e)); }
  };

  const catLabel = (slug) => taxonomy.categories.find((c) => c.slug === slug)?.label || slug;

  return (
    <div data-testid="admin-parts-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 style={{ fontSize: 28 }}>Auto Parts</h1>
          <p className="vd-muted">{parts.length} parts in catalogue</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/parts/import" className="vd-btn-secondary" data-testid="admin-parts-import-btn"><UploadCloud size={16} /> Import CSV</Link>
          <Link to="/admin/parts/new" className="vd-btn-primary" data-testid="admin-add-part-btn"><Plus size={16} /> Add Part</Link>
        </div>
      </div>

      <div className="vd-card p-4 mb-5 flex items-center gap-4" data-testid="admin-parts-toolbar">
        <div className="flex items-center gap-3 flex-1">
          <Search size={16} color="var(--text-muted)" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by name, part number or brand…"
            className="vd-input"
            style={{ border: "none", padding: 0, height: "auto" }}
            data-testid="admin-parts-search-input"
          />
        </div>
        <select className="vd-select" style={{ width: 220 }} value={category} onChange={(e) => setCategory(e.target.value)} data-testid="admin-parts-cat-select">
          <option value="">All categories</option>
          {taxonomy.categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
        </select>
      </div>

      {err && <p className="text-sm mb-3" style={{ color: "var(--primary-red)" }}>{err}</p>}

      <div className="vd-card overflow-x-auto" data-testid="admin-parts-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left vd-muted uppercase text-xs tracking-wider border-b" style={{ borderColor: "var(--border-color)" }}>
              <th className="px-4 py-3">Part</th>
              <th>Category</th>
              <th>Cond.</th>
              <th>P/N</th>
              <th>Price</th>
              <th>Stock</th>
              <th>Visible</th>
              <th className="text-right pr-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="px-4 py-8 text-center vd-muted">Loading…</td></tr>
            ) : parts.length === 0 ? (
              <tr><td colSpan="8" className="px-4 py-8 text-center vd-muted">No parts yet.</td></tr>
            ) : parts.map((p) => (
              <tr key={p.id} className="border-t" style={{ borderColor: "var(--border-color)" }} data-testid={`admin-part-row-${p.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {p.image && <img src={p.image.startsWith("http") ? p.image : `${process.env.REACT_APP_BACKEND_URL}${p.image}`} alt="" className="w-10 h-10 rounded object-cover" style={{ background: "var(--bg-secondary)" }} />}
                    <div>
                      <p className="vd-heading font-medium truncate max-w-[280px]">{p.name}</p>
                      <p className="vd-muted text-xs">{p.brand || "—"}</p>
                    </div>
                  </div>
                </td>
                <td className="vd-muted">{catLabel(p.category)}</td>
                <td className="vd-muted">{p.condition}</td>
                <td className="vd-muted">{p.part_number || "—"}</td>
                <td className="vd-heading font-semibold">£{Number(p.price).toFixed(2)}</td>
                <td><span style={{ color: p.stock < 2 ? "var(--primary-red)" : "var(--text-heading)", fontWeight: 600 }}>{p.stock}</span></td>
                <td>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold rounded px-2 py-0.5" style={{ background: p.is_visible ? "rgba(0,180,80,0.15)" : "rgba(230,0,0,0.10)", color: p.is_visible ? "#00A35C" : "var(--primary-red)" }}>
                    {p.is_visible ? "Live" : "Hidden"}
                  </span>
                </td>
                <td className="pr-4">
                  <div className="flex items-center gap-1 justify-end">
                    <Link to={`/admin/parts/${p.id}`} className="p-2 rounded hover:bg-[var(--bg-secondary)]" title="Edit" data-testid={`part-edit-${p.id}`}><Pencil size={14} /></Link>
                    <button onClick={() => doDuplicate(p.id)} className="p-2 rounded hover:bg-[var(--bg-secondary)]" title="Duplicate" data-testid={`part-duplicate-${p.id}`}><Copy size={14} /></button>
                    <button onClick={() => doToggle(p.id)} className="p-2 rounded hover:bg-[var(--bg-secondary)]" title="Toggle visibility" data-testid={`part-toggle-${p.id}`}>
                      {p.is_visible ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button onClick={() => doDelete(p.id)} className="p-2 rounded hover:bg-[var(--bg-secondary)]" title="Delete" data-testid={`part-delete-${p.id}`}><Trash2 size={14} color="var(--primary-red)" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminParts;
